import { makeAutoObservable, runInAction } from "mobx";
import { IDefaultOfferRanges, IResolution } from "../../../models/ResolutionTypes";
import ResolutionsService, { IExchangeRefundProviderOption, IRefundProviderOption, IResolutionSettings } from "../../../services/api/resolutions-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import {ISaveable, WithOld} from "../../../stores/types";
import { ITranslationScopeContext } from "../../../translation/translation-scope";
import {
    ALLOW_OUT_OF_STOCK_EXCHANGE_OPTION,
    CREDIT_AMOUNT_TYPE,
    INSTANT_REFUND_PROVIDER,
    REFUND_BY,
    RESOLUTION_ID,
    RESOLUTION_TYPE,
    RETURN_ALTERNATIVE,
    VOUCHER_TYPE
} from '../constants';
import resolutionListStore from "./resolution-list-store";
import { ShippingSettingsStore } from "./shipping-settings-store";

export interface IResolutionChanges extends Partial<IResolution>{
    _client_id?: number|string;
};

class ResolutionStore implements ISaveable {
    private _changes: IResolutionChanges = {};
    private _model: IResolution = {} as IResolution;
    private _isSaving: boolean = false;
    private _shippingSettingsStore: ShippingSettingsStore;

    private _settings: IResolutionSettings = {} as IResolutionSettings;
    private _currentTranslationKeys: string[] = [];

    private readonly _translatableFields: string[] = [
        'name',
        'description',
    ];

	constructor(
        private _discardResolution: (store: ResolutionStore) => void,
        model: IResolution,
        settings: IResolutionSettings
    ) {
        makeAutoObservable(this);
        this._shippingSettingsStore = new ShippingSettingsStore();
        this._settings = settings;
        this._setModel(model);
	}

    private get resolutionService(): ResolutionsService {
		return rootContainer.get<ResolutionsService>(TYPE.ResolutionsService);
	}

    public isSaving(): boolean { return this._isSaving; }

    public isIdEquals(id?:string|number): boolean {
        return this._model.id === Number(id) || this._changes._client_id === (id ?? '').toString();
    }

    public get id() { return this._changes._client_id ?? this._model.id; }
    public get internalName() { return this._changes.internal_name ?? this._model.internal_name; }
    public get name() { return this._changes.name ?? this._model.name; }
    public get description() { return this._changes.description ?? this._model.description; }
    public get resolutionId() { return this._changes.resolution_id ?? this._model.resolution_id; }
    public get resolutionType() { return this._changes.resolution_type ?? this._model.resolution_type; }
    public get rangeFrom() { return this._changes.range_from ?? this._model.range_from; }
    public get rangeTo() { return this._changes.range_to ?? this._model.range_to; }
    public get modifyCreditAmount() { return this._changes.modify_credit_amount ?? this._model.modify_credit_amount; }
    public get isPercentage() { return this._changes.is_percentage ?? this._model.is_percentage; }
    public get creditAmountType() {
        return this._changes.credit_amount_type ?? this._model.credit_amount_type ?? CREDIT_AMOUNT_TYPE.PAID_AMOUNT;
    }
    public get voucherType() { return this._changes.voucher_type ?? this._model.voucher_type; }
    public get isHidden() { return this._changes.is_hidden ?? this._model.is_hidden; }
    public get type() { return this._changes.resolution_type ?? this._model.resolution_type; }
    public get isReturnGo() { return this._changes.is_return_go ?? this._model.is_return_go; }
    public get presentMoreExpensive() { return this._changes.present_more_expensive ?? this._model.present_more_expensive; }
    public get indicateLessExpensive() { return this._changes.indicate_less_expensive ?? this._model.indicate_less_expensive; }
    public get allowOutOfStockExchanging() { return this._changes.allow_out_of_stock_exchanging ?? this._model.allow_out_of_stock_exchanging; }
    public get sizeChartUrl() { return this._changes.size_chart_url ?? this._model.size_chart_url; }
    public get sizeOptionName() { return this._changes.size_option_name ?? this._model.size_option_name; }
    public get defaultShippingLineTitle() { return this._changes.default_shipping_line_title ?? this._model.default_shipping_line_title; }
    public get image() { return this._changes.image ?? this._model.image; }
    public get excludeByGateway() { return this._changes.exclude_by_gateway ?? this._model.exclude_by_gateway; }
    public get allowAutoRefund() { return this._changes.allow_auto_refund ?? this._model.allow_auto_refund; }
    public get deleted() { return this._changes._deleted ?? false; }
    public get minimumAvailableStockThreshold() { return this._changes.minimum_available_stock_threshold ?? this._model.minimum_available_stock_threshold; }

    public get shippingSettingsStore(): ShippingSettingsStore { return this._shippingSettingsStore; }

    public setIsHidden(val:boolean) {
        this._changes.is_hidden = val;
    }

    public setInternalName(v:string) {
        this._changes.internal_name = v;
    }

    public setRangeFrom(v:number) {
        this._changes.range_from = v;
    }

    public setRangeTo(v:number) {
        this._changes.range_to = v;
    }

    public setPresentMoreExpensive(v:boolean) {
        this._changes.present_more_expensive = v;
    }

    public setIndicateLessExpensive(v:boolean) {
        this._changes.indicate_less_expensive = v;
    }

    public setAllowOutOfStockExchanging(v:ALLOW_OUT_OF_STOCK_EXCHANGE_OPTION) {
        this._changes.allow_out_of_stock_exchanging = v;
    }

    public setSizeChartUrl(v:string) {
        this._changes.size_chart_url = v;
    }

    public setSizeOptionName(v:string) {
        this._changes.size_option_name = v;
    }

    public setDefaultShippingLineTitle(v:string) {
        this._changes.default_shipping_line_title = v;
    }

    public get instantRefundProvider() { 
        /** explicitly checking null b.c we set explicitly to null in setRefundBy method, 
         * when null it means we unset instant_refund_provider field and should not show instant_refund_provider from model */
        if (this._changes.instant_refund_provider === null) {
            return null;
        }
        return this._changes.instant_refund_provider ?? this._model.instant_refund_provider; 
    }

    private get refundBy() { 
        return this._changes.refund_by ?? this._model.refund_by; 
    }

    public get selectedRefundBy() { 
        return this.instantRefundProvider ?? this.refundBy; 
    }

    public setRefundBy(v: IExchangeRefundProviderOption) {
        /** when IExchangeRefundProviderOption has a refund_by field on it - we set the refund_by field by this value 
         * and instant_refund_provider by the actual value from IExchangeRefundProviderOption
         * for Rise integration we should have { ... refund_by:REFUND_BY.STORE_CREDIT, instant_refund_provider:'rise' ... }
         */
        if (v.refund_by) {
            this._changes.refund_by = v.refund_by;
            this._changes.instant_refund_provider = v.value as INSTANT_REFUND_PROVIDER;
        } 
        else {
            this._changes.refund_by = v.value as REFUND_BY;
            this._changes.instant_refund_provider = null;
        }
    }

    public setImage(v: number) {
        this._changes.image = v;
    }

    private setResolutionId(v: RESOLUTION_ID) {
        this._changes.resolution_id = v;
    }

    public setExcludeByGateway(v: boolean) {
        this._changes.exclude_by_gateway = v;
    }

    public setCreditAmountType(v: CREDIT_AMOUNT_TYPE) {
        this._changes.credit_amount_type = v;
    }

    public setIsPercentage(v: boolean) {
        this._changes.is_percentage = v;
    }

    public setModifyCreditAmount(v: number) {
        this._changes.modify_credit_amount = parseFloat(v.toFixed(2));
    }

    public setAllowAutoRefund(v: boolean) {
        this._changes.allow_auto_refund = v;
    }

    public setInstantRefundProvider(v: IRefundProviderOption) {
        this._changes.instant_refund_provider = v.value as INSTANT_REFUND_PROVIDER;
        this._changes.resolution_type = v.resolution_type as RESOLUTION_TYPE;
        this._mapResolutionIdByType(this._changes.resolution_type);
    }

    /** used only in Return Resolutions */
    public setResolutionType(v: RESOLUTION_TYPE) {
        this._changes.resolution_type = v;
        this._mapResolutionIdByType(this._changes.resolution_type);

        /** unset instant_refund_provider when switching resolution types 
         * exchange resolutions set this value through refund_by field  */
        this._changes.instant_refund_provider = null;

        if (this._changes.resolution_type === RESOLUTION_TYPE.InstantRefundType) {
            /** when there is only one refund service provider - select it by default */
            if (this._settings.refund_service_providers.length === 1) {
                this.setInstantRefundProvider(this._settings.refund_service_providers[0] as IRefundProviderOption);
            }
        }
    }

    private _mapResolutionIdByType(v: RESOLUTION_TYPE) {
        // if resolution type is one of listed in cases, then adjust resolution id accordingly
        switch (v) {
            case RESOLUTION_TYPE.Refund:
                this.setResolutionId(RESOLUTION_ID.ORIG_PAY_REFUND);
                break;
            case RESOLUTION_TYPE.BankRefund:
                this.setResolutionId(RESOLUTION_ID.REFUND_TO_BANK);
                break;
            case RESOLUTION_TYPE.GiftCard:
                this.setResolutionId(RESOLUTION_ID.REFUND_TO_GIFT_CARD);
                break;
            case RESOLUTION_TYPE.Credits:
                this.setResolutionId(RESOLUTION_ID.REFUND_TO_CREDIT);
                break;
            case RESOLUTION_TYPE.InstantRefundType:
                this.setResolutionId(RESOLUTION_ID.INSTANT_REFUND);
                break;
            default: 
                throw new Error(`unknown resolution type ${v}`);
        }
    }

    public setVoucherType(v: VOUCHER_TYPE) {
        this._changes.voucher_type = v;
    }

    public setMinimumAvailableStockThreshold(v: string) {
        if (!isNaN(Number(v))) {
            this._changes.minimum_available_stock_threshold = Number(v);
        }
    }

    public getResolutionTypeName(): string {
        if (this.instantRefundProvider) {
            return 'Refund Services';
        }

        switch (this.resolutionType) {
            case RESOLUTION_TYPE.Credits:
                return 'Credits';
            case RESOLUTION_TYPE.Refund:
                return 'Refund';
            case RESOLUTION_TYPE.DirectVariantExchange:
                return 'Variant exchange';
            case RESOLUTION_TYPE.BankRefund:
                return 'Bank Refund';
            case RESOLUTION_TYPE.CancelOrderType:
                return 'Cancel Order';
            case RESOLUTION_TYPE.ManualExchangeType:
                return 'Text';
            case RESOLUTION_TYPE.GiftCard:
                return 'Gift Card';
            case RESOLUTION_TYPE.ProductExchangeType:
                return 'Product Exchange';
            case RESOLUTION_TYPE.InstantRefundType:
                return 'Instant Refund';
            case RESOLUTION_TYPE.NoOffersType:
                return 'No Offers';
            default:
                return '';
        }
    }

    public getOfferRanges(): [number, number] {
        const defaultOfferRanges: IDefaultOfferRanges = resolutionListStore.settings.default_offer_ranges;
        let alternative = RETURN_ALTERNATIVE.KEEP;

        if (this.isReturnGo || this.isAutomaticExchangeType()) {
            alternative = this.getSolutionType();
            return defaultOfferRanges[alternative];
        }

        return defaultOfferRanges[alternative];
    }

    private getSolutionType() {
        switch (this.resolutionId) {
            case RESOLUTION_ID.SHIP_LATER:
                return RETURN_ALTERNATIVE.SHIP_LATER;
            case RESOLUTION_ID.KEEP:
                return RETURN_ALTERNATIVE.KEEP;
            case RESOLUTION_ID.DONATE:
                return RETURN_ALTERNATIVE.DONATE;
            case RESOLUTION_ID.ORIG_PAY_REFUND:
                return RETURN_ALTERNATIVE.ORIG_PAY_REFUND;
            case RESOLUTION_ID.REFUND_TO_CREDIT:
                return RETURN_ALTERNATIVE.REFUND_TO_CREDIT;
            case RESOLUTION_ID.REFUND_TO_GIFT_CARD:
                return RETURN_ALTERNATIVE.REFUND_TO_GIFT_CARD;
            case RESOLUTION_ID.INSTANT_VARIANT_EXCHANGE:
                return RETURN_ALTERNATIVE.INSTANT_VARIANT_EXCHANGE;
            case RESOLUTION_ID.REFUND_TO_BANK:
                return RETURN_ALTERNATIVE.BANK_REFUND;
            case RESOLUTION_ID.CANCEL_ORDER:
                return RETURN_ALTERNATIVE.CANCEL_ORDER;
            case RESOLUTION_ID.MANUAL_EXCHANGE:
                return RETURN_ALTERNATIVE.MANUAL_EXCHANGE;
            case RESOLUTION_ID.PRODUCT_EXCHANGE:
                return RETURN_ALTERNATIVE.PRODUCT_EXCHANGE;
            case RESOLUTION_ID.INSTANT_REFUND:
                return RETURN_ALTERNATIVE.INSTANT_REFUND;
            case RESOLUTION_ID.NO_OFFERS:
                return RETURN_ALTERNATIVE.NO_OFFERS;
        }
    }

    private isAutomaticExchangeType(): boolean {
        return [
            RESOLUTION_TYPE.DirectVariantExchange,
            RESOLUTION_TYPE.ProductExchangeType
        ].includes(this.type);
    }

    public isReturnScore(): boolean {
        return [
            RESOLUTION_ID.SHIP_LATER,
            RESOLUTION_ID.KEEP,
            RESOLUTION_ID.DONATE
        ].includes(this.resolutionId);
    };

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length || this.shippingSettingsStore.hasChanges();
    }

    public setChanges(changes: IResolutionChanges): void {
        this._changes = changes;
    }

    private _setModel(model: IResolution): void {
        this._model = model;
        this._changes = {} as IResolutionChanges;
        this._shippingSettingsStore.setModel(model.resolution_shipping_setting);
    }

    private getUpdatedModel(): IResolution {
        const model = { ...this._model, ...this._changes };
        model.resolution_shipping_setting = this._shippingSettingsStore.getUpdatedModel();
        return model;
    }

    public async save(): Promise<void>  {
        const updatedModel: WithOld<IResolution> = this.getUpdatedModel();
        this._isSaving = true;

        let savedResolution: IResolution | undefined = undefined;
        try {
            if (updatedModel.id) {
                updatedModel.__old = this._model;
                savedResolution = await this.resolutionService.updateResolution(updatedModel.id, updatedModel);
            } else {
                savedResolution = await this.resolutionService.createResolution(updatedModel);
            }
        } catch (e) {
            // throw new Error(`ResolutionStore.save: ${e}`);
            console.error(`ResolutionStore.save: ${e}`);
        }

        runInAction(() => {
            if (savedResolution) {
                this._setModel(savedResolution);
            }
            this._isSaving = false;
        });

    }

    public discardChanges(): void {
        if (!this._model.id) {
            this._discardResolution(this);
        }
        else {
            this._changes = {} as IResolution;
        }
        this._shippingSettingsStore.discardChanges();
    }

    public getTranslationKeys(): string[] {
        const keyList: string[] = [];

        if (this.deleted) {
            return keyList;
        }

        this._translatableFields.forEach(field => {
            const key = this[field as keyof ResolutionStore];
            if (key) {
                keyList.push(key.toString());
            }
        });

        /** we need this._currentTranslationKeys property to use it as dependency in react hooks,
         * so that the hook method will run only when the list was changed */
        const isEqualArrays = (this._currentTranslationKeys.length === keyList.length
            && this._currentTranslationKeys.every(k => !!keyList.find(key => key === k)));
        if (!isEqualArrays) {
            this._currentTranslationKeys = keyList;
        }

        return this._currentTranslationKeys;
    }

    public setDeleted(setDeletedTranslationKeys: ITranslationScopeContext['setDeletedKeys']) {
        const keyList = this.getTranslationKeys();
        setDeletedTranslationKeys(keyList);

        this._changes._deleted = true;
    }

    public async delete(): Promise<IResolution> {
        return await this.resolutionService.deleteResolution(this._model.id);
    }
}

export default ResolutionStore;